/* eslint-disable max-lines, max-lines-per-function, max-statements */
import { useLazyQuery } from "@apollo/client";
import prepareComponent from "@bluframe/grapple/prepareComponent";
import { queries } from "@bluframe/mightychroma-mightytool-graphql-schema";
import config from "config/site";
import useActionsCheckbox from "hooks/actions/useActionsCheckbox";
import useClearHomeVideoReviewsListButton from "hooks/actions/useClearHomeVideoReviewsListButton";
import useListButton from "hooks/actions/useListButton";
import useLoadMoreButton from "hooks/actions/useLoadMoreButton";
import useAddHomeVideoReview from "hooks/useAddHomeVideoReview";
import useCreateReviews from "hooks/useCreateReviews";
import useImageSelector from "hooks/useImageSelector";
import useReviews from "hooks/useReviews";
import useTechSpecsSelector from "hooks/useTechSpecsSelector";
import useUser from "hooks/useUser";
import { useCallback, useEffect, useMemo, useState } from "react";
import Add from "./Add";
import useHomeVideoReviewCard from "./hooks/useHomeVideoReviewCard";
import useHomeVideoReviewServiceListControls from "./hooks/useHomeVideoReviewServiceListControls";
const { homeVideos: { SEARCH_HOME_VIDEOS } } = queries;
const DEFAULT_IS_COMPACT = true;
/**
 * usePrepareComponent
 *
 * @description
 * Prepares all props for the Add screen by wiring up the reviews context,
 * control checkboxes, form props, per-card actions, per-card review data,
 * and list controls.
 *
 * @returns {ComponentProps} Props for the Add component
 *
 */
export const usePrepareComponent = () => {
    const { clear, setIsAddHomeVideo, setIsMatchTitle, setIsUHD, setName, setReview, setHomeVideoId, setIMDbId, state } = useReviews();
    const { homeVideoId, isAddHomeVideo } = state.homeVideoReview;
    const { isAdmin, isEditor } = useUser();
    const [isCompact, setIsCompact] = useState(DEFAULT_IS_COMPACT);
    useEffect(() => {
        if (!(isAdmin || isEditor) && state.isUHD !== false) {
            setIsUHD(false);
        }
    }, [isAdmin, state.isUHD, setIsUHD, isEditor]);
    const { areReviewsLoading } = useCreateReviews();
    // Control checkboxes
    const onClear = useCallback((serviceName) => () => {
        clear(serviceName);
    }, [clear]);
    const onSetReview = useCallback((serviceName) => (review) => {
        setReview(review, serviceName);
    }, [setReview]);
    const reviewsControlCheckboxesProps = useMemo(() => {
        const isAddHomeVideoProps = {
            checked: state.homeVideoReview.isAddHomeVideo,
            isDisabled: areReviewsLoading,
            onChange: () => {
                setIsAddHomeVideo(!state.homeVideoReview.isAddHomeVideo);
            }
        };
        const isMatchTitleCheckboxProps = {
            checked: state.isMatchTitle,
            isDisabled: areReviewsLoading,
            onChange: () => {
                setIsMatchTitle(!state.isMatchTitle);
            }
        };
        const isUHDCheckboxProps = {
            checked: state.isUHD,
            isDisabled: areReviewsLoading,
            onChange: () => {
                setIsUHD(!state.isUHD);
            }
        };
        const isCompactCheckboxProps = {
            checked: isCompact,
            isDisabled: areReviewsLoading,
            onChange: () => {
                setIsCompact(!isCompact);
            }
        };
        return {
            isAddHomeVideoProps,
            isAdmin,
            isCompactCheckboxProps,
            isEditor,
            isMatchTitleCheckboxProps,
            isUHDCheckboxProps
        };
    }, [
        state.homeVideoReview.isAddHomeVideo,
        state.isMatchTitle,
        state.isUHD,
        areReviewsLoading,
        isCompact,
        isAdmin,
        isEditor,
        setIsAddHomeVideo,
        setIsMatchTitle,
        setIsUHD
    ]);
    // Form props (AddActions logic)
    const { IMDbId, hasSelectedIcon, isAddHomeVideoReviewEnabled, isImdbIdValid, loading: formLoading, name: formName, onAddHomeVideoReview, onClearAll: onClearAllHook, onTmdbSearch, onTmdbSelect, tmdbSelectedId, tmdbSuggestions, tmdbLoading } = useAddHomeVideoReview();
    const imageSelector = useImageSelector({ serviceType: "reviews" });
    const screenshotSelector = useImageSelector({
        mediaType: "screenshots",
        serviceType: "reviews"
    });
    const techSpecsSelector = useTechSpecsSelector();
    const [searchHomeVideos, { data: searchHomeVideosData, loading: searchHomeVideosLoading }] = useLazyQuery(SEARCH_HOME_VIDEOS);
    const onImdbIdChange = useCallback((newIMDbId) => {
        setIMDbId(newIMDbId);
    }, [setIMDbId]);
    const onNameChange = useCallback((newName) => {
        setName(newName);
    }, [setName]);
    const onClearAll = useCallback(() => {
        onClearAllHook();
    }, [onClearAllHook]);
    const onGetHomeVideoSuggestions = useCallback(({ inputValue }) => {
        searchHomeVideos({
            variables: {
                searchHomeVideosInput: {
                    title: inputValue
                }
            }
        });
    }, [searchHomeVideos]);
    const searchHomeVideo = useMemo(() => ({
        homeVideoTitles: searchHomeVideosData?.searchHomeVideos,
        isLoading: searchHomeVideosLoading,
        isSearchHomeVideo: !isAddHomeVideo,
        onGetHomeVideoSuggestions,
        onSelectHomeVideoId: setHomeVideoId,
        selectedHomeVideoId: homeVideoId
    }), [
        homeVideoId,
        isAddHomeVideo,
        onGetHomeVideoSuggestions,
        searchHomeVideosData?.searchHomeVideos,
        searchHomeVideosLoading,
        setHomeVideoId
    ]);
    const addHomeVideoReviewActionsProps = useMemo(() => ({
        IMDb: {
            IMDbId,
            hasSelectedIcon,
            isImdbIdValid,
            onImdbIdChange,
            onTmdbSearch,
            onTmdbSelect,
            tmdbSelectedId,
            tmdbSuggestions,
            tmdbLoading
        },
        imageSelector,
        screenshotSelector,
        isAddHomeVideoReviewEnabled,
        isAdmin,
        isEditor,
        label: "Add Home Video Review",
        loading: formLoading,
        name: formName,
        onAddHomeVideoReview,
        onClearAll,
        onNameChange,
        searchHomeVideo,
        techSpecsSelector
    }), [
        IMDbId,
        hasSelectedIcon,
        isImdbIdValid,
        onImdbIdChange,
        onTmdbSearch,
        onTmdbSelect,
        tmdbSelectedId,
        tmdbSuggestions,
        tmdbLoading,
        imageSelector,
        screenshotSelector,
        isAddHomeVideoReviewEnabled,
        isAdmin,
        isEditor,
        formLoading,
        formName,
        onAddHomeVideoReview,
        onClearAll,
        onNameChange,
        searchHomeVideo,
        techSpecsSelector
    ]);
    // Config
    const servicesConfig = useMemo(() => Object.freeze(config.reviews.flatMap((section) => section.rows.map((row) => ({
        name: row.name,
        serviceName: row.serviceName
    })))), []);
    // Per-card action checkboxes (one call per service, stable count)
    // Calls useActionsCheckbox 12 times (once per service) — stable count
    const actionsPropsByService = servicesConfig.reduce((acc, { serviceName }) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const listButtonProps = useListButton({
            serviceName,
            serviceType: "reviews"
        });
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const clearListButtonProps = useClearHomeVideoReviewsListButton({
            isLoading: listButtonProps.loading,
            serviceName
        });
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const onlyNewProps = useActionsCheckbox({
            inputId: `${serviceName}-isOnlyNew`,
            label: "Only New",
            serviceName,
            serviceType: "reviews",
            setValueName: "setIsOnlyNew",
            valueName: "isOnlyNew"
        });
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const onlyPresentProps = useActionsCheckbox({
            inputId: `${serviceName}-isOnlyPresent`,
            label: "Only Present",
            serviceName,
            serviceType: "reviews",
            setValueName: "setIsOnlyPresent",
            valueName: "isOnlyPresent"
        });
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const showFlaggedProps = useActionsCheckbox({
            inputId: `${serviceName}-isShowFlagged`,
            label: "Show Flagged",
            serviceName,
            serviceType: "reviews",
            setValueName: "setIsShowFlagged",
            valueName: "isShowFlagged"
        });
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const loadMoreProps = useLoadMoreButton({
            serviceName,
            serviceType: "reviews"
        });
        acc[serviceName] = {
            clearListButtonProps,
            listButtonProps,
            loadMoreProps,
            onlyNewProps,
            onlyPresentProps,
            serviceName,
            showFlaggedProps
        };
        return acc;
    }, {});
    // Per-card review data (one call per service, stable count)
    // Calls useHomeVideoReviewCard 12 times (once per service) — stable count
    const reviewsPropsByService = servicesConfig.reduce((acc, { name, serviceName }) => {
        const setIMDbIdForService = serviceName === "bluRay" || serviceName === "bluRayAuthority"
            ? setIMDbId
            : undefined;
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const card = useHomeVideoReviewCard({
            name,
            serviceName,
            setIMDbId: setIMDbIdForService
        });
        acc[serviceName] = {
            ...card,
            avatar: card.avatar || name.slice(0, 3)
        };
        return acc;
    }, {});
    // List controls
    const servicesListControlItem = useMemo(() => servicesConfig.map(({ serviceName }) => ({ serviceName })), [servicesConfig]);
    const { clearAllListsButtonProps, listAllButtonProps, isListAllLoading } = useHomeVideoReviewServiceListControls(servicesListControlItem);
    const mainProps = useMemo(() => ({
        actionsPropsByService,
        clearAllListsButtonProps,
        isAdmin,
        isEditor,
        isListAllLoading,
        listAllButtonProps,
        reviewsPropsByService,
        setIMDbId,
        services: config.reviews
    }), [
        actionsPropsByService,
        clearAllListsButtonProps,
        isAdmin,
        isEditor,
        isListAllLoading,
        listAllButtonProps,
        reviewsPropsByService,
        setIMDbId
    ]);
    return {
        areReviewsLoading,
        avForums: state.avForums.selected,
        avNirvana: state.avNirvana.selected,
        avsForum: state.avsForum.selected,
        bluRay: state.bluRay.selected,
        bluRayAuthority: state.bluRayAuthority.selected,
        digitalBits: state.digitalBits.selected,
        doBlu: state.doBlu.selected,
        hidefDigest: state.hidefDigest.selected,
        highDefDiscNews: state.highDefDiscNews.selected,
        homeTheaterForum: state.homeTheaterForum.selected,
        onClear,
        onSetReview,
        reviewsControlCheckboxesProps,
        addHomeVideoReviewActionsProps,
        mainProps,
        theaterByte: state.theaterByte.selected,
        whySoBlu: state.whySoBlu.selected
    };
};
export default prepareComponent(usePrepareComponent)(Add);
